import React, { useMemo, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Badge,
  Link,
  InputBase,
  IconButton,
  Tooltip,
} from '@mui/material';
import { useSelector } from 'react-redux';
import LinkIcon from '@mui/icons-material/Link';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

const QuestSidebar = ({
  quests,
  loading,
  loadingMore,
  hasMore,
  searchQuery,
  onSearchChange,
  selectedQuestId,
  onSelect,
  onViewAppeal,
  onLoadMore,
  sortOrder = 'desc',
  onSortToggle,
}) => {
  const { services } = useSelector((state) => state.quests);
  const lastElementRef = useRef(null);

  const getServiceName = (quest) => {
    if (quest.service?.name) return quest.service.name;
    if (services?.length) {
      const s = services.find((s) => s.id === quest.serviceId);
      if (s) return s.name;
    }
    return '';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      return new Date(dateString).toLocaleDateString('ru-RU');
    } catch {
      return dateString;
    }
  };

  const filtered = useMemo(() => {
    if (!quests) return [];
    let list = quests;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        (t) =>
          (t.clientName || '').toLowerCase().includes(q) ||
          (t.address || '').toLowerCase().includes(q) ||
          String(t.number || t.id).toLowerCase().includes(q)
      );
    }
    return list;
  }, [quests, searchQuery]);

  // Intersection Observer для бесконечной прокрутки
  useEffect(() => {
    if (!hasMore || !onLoadMore || loadingMore) return;

    const currentElement = lastElementRef.current;
    if (!currentElement) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          onLoadMore();
        }
      },
      { threshold: 0.5 }
    );

    observer.observe(currentElement);

    return () => {
      observer.unobserve(currentElement);
    };
  }, [hasMore, onLoadMore, loadingMore, filtered.length]);

  const listToolbar = (
    <Box
      sx={{
        p: 1.5,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Tooltip title={sortOrder === 'desc' ? 'Сначала новые' : 'Сначала старые'}>
        <IconButton
          size="small"
          onClick={onSortToggle}
          sx={{
            bgcolor: 'grey.100',
            borderRadius: 1,
            p: 0.75,
            '&:hover': { bgcolor: 'grey.200' },
          }}
        >
          {sortOrder === 'desc' ? (
            <ArrowDownwardIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
          ) : (
            <ArrowUpwardIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
          )}
        </IconButton>
      </Tooltip>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          bgcolor: 'grey.100',
          borderRadius: 1,
          px: 1.5,
          py: 0.5,
          flex: 1,
        }}
      >
        <SearchIcon sx={{ color: 'text.secondary', fontSize: 20, mr: 1 }} />
        <InputBase
          placeholder="Поиск"
          value={searchQuery || ''}
          onChange={(e) => onSearchChange && onSearchChange(e.target.value)}
          sx={{ fontSize: '0.875rem', flex: 1 }}
        />
        {searchQuery && (
          <IconButton size="small" onClick={() => onSearchChange && onSearchChange('')}>
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        )}
      </Box>
    </Box>
  );

  if (loading && filtered.length === 0) {
    return (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {listToolbar}
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress size={28} />
        </Box>
      </Box>
    );
  }

  if (filtered.length === 0) {
    return (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {listToolbar}
        <Box sx={{ textAlign: 'center', py: 4, px: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Заявки не найдены
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {listToolbar}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          '&::-webkit-scrollbar': { width: 6 },
          '&::-webkit-scrollbar-thumb': { bgcolor: 'grey.300', borderRadius: 3 },
        }}
      >
        {filtered.map((quest, index) => {
          const isSelected = selectedQuestId === quest.id;
          const isLastElement = index === filtered.length - 1;
          return (
            <Box
              key={quest.id}
              ref={isLastElement ? lastElementRef : null}
              onClick={() => onSelect && onSelect(quest)}
              sx={{
                p: 2,
                px: 2.5,
                cursor: 'pointer',
                borderLeft: isSelected ? '3px solid' : '3px solid transparent',
                bgcolor: isSelected ? 'primary.50' : 'transparent',
                borderBottom: '1px solid',
                borderColor: 'divider',
                transition: 'all 0.15s',
                '&:hover': {
                  bgcolor: isSelected ? 'primary.50' : 'grey.50',
                },
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Typography variant="subtitle2" fontWeight={600} noWrap sx={{ flex: 1 }}>
                  №{quest.number || quest.id} - {quest.clientName || 'Клиент'}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ ml: 1, flexShrink: 0 }}>
                  {formatDate(quest.createdAt || quest.createdDate)}
                </Typography>
              </Box>

              {quest.address && (
                <Typography variant="body2" color="text.secondary" noWrap sx={{ mt: 0.25 }}>
                  {quest.address}
                </Typography>
              )}

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
                <Typography variant="caption" color="text.secondary">
                  Тип заявки: {getServiceName(quest)}
                </Typography>
                {quest.relatedTicketId && (
                  <Link
                    component="button"
                    variant="caption"
                    onClick={(e) => {
                      e.stopPropagation();
                      onViewAppeal?.(quest.relatedTicketId);
                    }}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      textDecoration: 'none',
                      color: 'primary.main',
                      '&:hover': { textDecoration: 'underline' },
                    }}
                  >
                    <LinkIcon sx={{ fontSize: 14 }} />
                    Обращение
                  </Link>
                )}
                {quest.unreadCount > 0 && (
                  <Badge
                    badgeContent={quest.unreadCount}
                    color="primary"
                    sx={{
                      '& .MuiBadge-badge': {
                        bgcolor: 'primary.main',
                        color: '#fff',
                        fontSize: '0.7rem',
                        height: 20,
                        minWidth: 20,
                      },
                    }}
                  />
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
      {loadingMore && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <CircularProgress size={24} />
          <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
            Загрузка...
          </Typography>
        </Box>
      )}
      {!hasMore && filtered.length > 0 && (
        <Box sx={{ textAlign: 'center', py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Typography variant="body2" color="text.secondary">
            Все заявки загружены
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default QuestSidebar;
